import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import socket from '../utils/socket';
import '../ui-pages.css';
import '../campaigns.css';
import { SkelTable, SkelStats } from '../components/Skeletons';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } });
const fmt = (d) => d ? new Date(d).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const HEADER_TO_MEDIA = { IMAGE: 'image', DOCUMENT: 'document', VIDEO: 'video' };

export default function Campaigns() {
  const { workspaceUuid } = useParams();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // campaign id in analytics view
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API_URL}/api/campaigns?workspaceUuid=${workspaceUuid}`, auth()); setList(r.data.items || []); }
    catch { setList([]); } finally { setLoading(false); }
  }, [workspaceUuid]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!workspaceUuid) return;
    socket.emit('joinWorkspace', workspaceUuid);
    const on = () => load();
    socket.on('campaign:progress', on); socket.on('campaign:click', on);
    return () => { socket.off('campaign:progress', on); socket.off('campaign:click', on); };
  }, [workspaceUuid, load]);

  const send = async (c) => {
    if (!window.confirm(`Send "${c.name}" to ${c.totalRecipients} recipients now?`)) return;
    try { await axios.post(`${API_URL}/api/campaigns/${c._id}/send`, { workspaceUuid }, auth()); load(); }
    catch (e) { alert('Send failed: ' + (e.response?.data?.message || e.message)); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    await axios.delete(`${API_URL}/api/campaigns/${c._id}?workspaceUuid=${workspaceUuid}`, auth()); load();
  };

  if (selected) return <Analytics workspaceUuid={workspaceUuid} campaignId={selected} onBack={() => { setSelected(null); load(); }} />;

  return (
    <div className="ui-page">
      <header className="ui-head">
        <div>
          <h1 className="ui-title">Campaigns</h1>
          <p className="ui-sub">WhatsApp marketing campaigns with per-recipient click & order tracking.</p>
        </div>
        <div className="ui-head-actions">
          <button className="cmp-btn primary" onClick={() => setShowNew(true)}>+ New Campaign</button>
        </div>
      </header>

      {loading ? <SkelTable rows={8} cols={6} />
        : list.length === 0 ? (
          <div className="ui-empty">
            <div className="ui-empty-ic" style={{ color: '#9C978C' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11l14-6v14L3 13z" /><path d="M3 11v3a2 2 0 0 0 2 2h1" /><path d="M17 8a4 4 0 0 1 0 8" /></svg>
            </div>
            <h2>No campaigns yet</h2>
            <p>Create one to send an approved template to a CSV list and track clicks & orders.</p>
            <button className="cmp-btn primary" style={{ marginTop: 14 }} onClick={() => setShowNew(true)}>+ New Campaign</button>
          </div>
        ) : (
          <div className="cmp-card">
            <div className="cmp-table-wrap">
              <table className="cmp-table">
                <thead><tr><th>Campaign</th><th>Template</th><th>Recipients</th><th>Sent</th><th>Clicked</th><th>Orders</th><th>Status</th><th>Created</th><th /></tr></thead>
                <tbody>
                  {list.map(c => (
                    <tr key={c._id} className="cmp-row" onClick={() => setSelected(c._id)}>
                      <td className="cmp-name">{c.name}</td>
                      <td className="cmp-muted">{c.templateName}</td>
                      <td className="cmp-num">{c.totalRecipients}</td>
                      <td className="cmp-num">{c.sentCount}{c.status === 'sending' ? `/${c.totalRecipients}` : ''}</td>
                      <td className="cmp-num">{c.clickedCount}</td>
                      <td className="cmp-num">{c.orderedCount}</td>
                      <td><span className={`cmp-badge s-${c.status}`}>{c.status}</span></td>
                      <td className="cmp-muted">{fmt(c.createdAt)}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {c.status === 'draft' && <button className="cmp-btn sm primary" onClick={() => send(c)}>Send</button>}
                        {c.status === 'sending' && <button className="cmp-btn sm ghost" onClick={() => axios.post(`${API_URL}/api/campaigns/${c._id}/pause`, { workspaceUuid }, auth()).then(load)}>Pause</button>}
                        {c.status === 'paused' && <button className="cmp-btn sm primary" onClick={() => axios.post(`${API_URL}/api/campaigns/${c._id}/resume`, { workspaceUuid }, auth()).then(load)}>Resume</button>}
                        <button className="cmp-btn sm danger" onClick={() => del(c)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {showNew && <NewCampaign workspaceUuid={workspaceUuid} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

// ---------------------------------------------------------------- new campaign
function NewCampaign({ workspaceUuid, onClose, onCreated }) {
  const [templates, setTemplates] = useState([]);
  const [name, setName] = useState('');
  const [tpl, setTpl] = useState(null);
  const [vars, setVars] = useState([]);
  const [media, setMedia] = useState(null);      // {mediaId, filename}
  const [uploading, setUploading] = useState(false);
  const [dest, setDest] = useState('https://zarposhcollection.com/');
  const [wait, setWait] = useState(10);
  const [csvText, setCsvText] = useState('');
  const [csvCount, setCsvCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/campaigns/templates?workspaceUuid=${workspaceUuid}`, auth())
      .then(r => setTemplates(r.data.templates || [])).catch(() => setTemplates([]));
  }, [workspaceUuid]);

  const pickTemplate = (nameSel) => {
    const t = templates.find(x => x.name === nameSel) || null;
    setTpl(t); setMedia(null);
    setVars(t ? Array(t.varCount).fill('') : []);
  };

  const onCsv = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      setCsvText(text);
      const count = text.split(/\r?\n/).map(l => (l.split(',')[0] || '').replace(/[^\d+]/g, '')).filter(p => p.length >= 8).length;
      setCsvCount(count);
    };
    reader.readAsText(file);
  };

  const uploadMedia = async (file) => {
    setUploading(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('workspaceUuid', workspaceUuid); fd.append('file', file);
      const r = await axios.post(`${API_URL}/api/campaigns/media`, fd, auth());
      setMedia({ mediaId: r.data.mediaId, filename: r.data.filename, mimetype: r.data.mimetype });
    } catch (e) { setErr('Media upload failed: ' + (e.response?.data?.message || e.message)); }
    finally { setUploading(false); }
  };

  const needsMedia = tpl && HEADER_TO_MEDIA[tpl.headerFormat];
  const canCreate = name.trim() && tpl && csvText.trim() && (!needsMedia || media) && vars.every(v => v.trim());

  const create = async () => {
    setBusy(true); setErr('');
    try {
      const body = {
        workspaceUuid, name: name.trim(), templateName: tpl.name, language: tpl.language || 'en',
        variables: vars, waitSeconds: wait, csvText,
        mediaType: needsMedia ? HEADER_TO_MEDIA[tpl.headerFormat] : 'none',
        mediaId: media?.mediaId || '', mediaFilename: media?.filename || '',
        buttonType: tpl.buttonType, buttonDestination: tpl.buttonType !== 'none' ? dest : '',
      };
      const r = await axios.post(`${API_URL}/api/campaigns`, body, auth());
      alert(`Campaign created as a draft with ${r.data.recipients} recipients. Review it in the list, then hit Send.`);
      onCreated();
    } catch (e) { setErr(e.response?.data?.message || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="cmp-scrim" onClick={onClose}>
      <div className="cmp-modal" onClick={e => e.stopPropagation()}>
        <div className="cmp-modal-head"><h3>New Campaign</h3><button onClick={onClose}>✕</button></div>
        <div className="cmp-modal-body">
          {err && <div className="cmp-err">{err}</div>}

          <label className="cmp-label">Campaign name</label>
          <input className="cmp-input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Jahanara Launch — Aug" />

          <label className="cmp-label">Approved template</label>
          <select className="cmp-input" value={tpl?.name || ''} onChange={e => pickTemplate(e.target.value)}>
            <option value="">Select a template…</option>
            {templates.map(t => <option key={t.name} value={t.name}>{t.name} {t.buttonType === 'dynamic' ? '· trackable' : ''}</option>)}
          </select>

          {tpl && (
            <>
              <div className="cmp-tpl-preview">{tpl.bodyText}</div>
              {tpl.buttonType === 'dynamic'
                ? <div className="cmp-hint ok">This template has a trackable link — per-recipient clicks will be recorded.</div>
                : tpl.buttonType === 'static'
                  ? <div className="cmp-hint warn">This template's link is static — sends & orders track, but individual clicks won't.</div>
                  : null}

              {vars.length > 0 && <label className="cmp-label">Variable values</label>}
              {vars.map((v, i) => (
                <input key={i} className="cmp-input" value={v} placeholder={`{{${i + 1}}}`}
                  onChange={e => setVars(prev => prev.map((x, j) => j === i ? e.target.value : x))} />
              ))}

              {needsMedia && (
                <>
                  <label className="cmp-label">{tpl.headerFormat} header file</label>
                  {media ? <div className="cmp-file">{media.filename}</div>
                    : <input type="file" className="cmp-input" disabled={uploading}
                        accept={tpl.headerFormat === 'IMAGE' ? 'image/*' : tpl.headerFormat === 'DOCUMENT' ? '.pdf' : 'video/*'}
                        onChange={e => e.target.files[0] && uploadMedia(e.target.files[0])} />}
                  {uploading && <div className="cmp-hint">Uploading…</div>}
                </>
              )}

              {tpl.buttonType !== 'none' && (
                <>
                  <label className="cmp-label">Button link destination</label>
                  <input className="cmp-input" value={dest} onChange={e => setDest(e.target.value)} />
                </>
              )}
            </>
          )}

          <label className="cmp-label">Phone numbers (CSV — one number per line, first column)</label>
          <input type="file" className="cmp-input" accept=".csv,.txt" onChange={e => e.target.files[0] && onCsv(e.target.files[0])} />
          {csvCount > 0 && <div className="cmp-hint ok">{csvCount} valid numbers detected</div>}
          <div className="cmp-or">or paste numbers</div>
          <textarea className="cmp-input" rows={4} placeholder="923001234567&#10;923009876543" value={csvText}
            onChange={e => { setCsvText(e.target.value); setCsvCount(e.target.value.split(/\r?\n/).map(l => (l.split(',')[0] || '').replace(/[^\d+]/g, '')).filter(p => p.length >= 8).length); }} />

          <label className="cmp-label">Wait between messages</label>
          <select className="cmp-input" value={wait} onChange={e => setWait(Number(e.target.value))}>
            <option value={5}>5 seconds</option>
            <option value={10}>10 seconds</option>
            <option value={15}>15 seconds</option>
          </select>
        </div>
        <div className="cmp-modal-foot">
          <button className="cmp-btn ghost" onClick={onClose}>Cancel</button>
          <button className="cmp-btn primary" disabled={!canCreate || busy} onClick={create}>{busy ? 'Creating…' : 'Create Campaign'}</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- analytics
function Analytics({ workspaceUuid, campaignId, onBack }) {
  const [c, setC] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [cr, rr] = await Promise.all([
        axios.get(`${API_URL}/api/campaigns/${campaignId}?workspaceUuid=${workspaceUuid}`, auth()),
        axios.get(`${API_URL}/api/campaigns/${campaignId}/recipients?workspaceUuid=${workspaceUuid}&limit=500${filter !== 'all' ? `&filter=${filter}` : ''}`, auth()),
      ]);
      setC(cr.data.campaign); setRows(rr.data.items || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [campaignId, workspaceUuid, filter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    socket.emit('joinWorkspace', workspaceUuid);
    const on = (p) => { if (!p?.campaignId || String(p.campaignId) === String(campaignId)) load(); };
    socket.on('campaign:progress', on); socket.on('campaign:click', () => load());
    return () => { socket.off('campaign:progress', on); socket.off('campaign:click'); };
  }, [workspaceUuid, campaignId, load]);

  if (loading || !c) return (
    <div className="ui-page">
      <header className="ui-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="cmp-btn ghost sm" onClick={onBack}>← Back</button>
          <div><h1 className="ui-title">Campaign</h1><p className="ui-sub">Loading recipient analytics…</p></div>
        </div>
      </header>
      <SkelStats count={4} />
      <SkelTable rows={8} cols={5} />
    </div>
  );

  const pct = (n) => c.totalRecipients ? Math.round((n / c.totalRecipients) * 100) : 0;
  const funnel = [
    { label: 'Sent', n: c.sentCount },
    { label: 'Delivered', n: c.deliveredCount },
    { label: 'Read', n: c.readCount },
    { label: 'Clicked link', n: c.clickedCount },
    { label: 'Placed order', n: c.orderedCount },
  ];

  return (
    <div className="ui-page">
      <header className="ui-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="cmp-btn ghost sm" onClick={onBack}>← Back</button>
          <div><h1 className="ui-title">{c.name}</h1><p className="ui-sub">{c.templateName} · launched {fmt(c.launchedAt)} · tracking orders until {fmt(c.attributionUntil)}</p></div>
        </div>
      </header>

      <div className="cmp-stats">
        <div className="cmp-stat"><div className="k">Messages Sent</div><div className="v">{c.sentCount}<small>/{c.totalRecipients}</small></div></div>
        <div className="cmp-stat"><div className="k">Links Opened</div><div className="v blue">{c.clickedCount}<small>{pct(c.clickedCount)}%</small></div></div>
        <div className="cmp-stat"><div className="k">Orders Placed</div><div className="v green">{c.orderedCount}<small>{pct(c.orderedCount)}%</small></div></div>
        <div className="cmp-stat"><div className="k">Revenue</div><div className="v">{c.revenueCurrency} {Math.round(c.revenue || 0).toLocaleString()}</div></div>
      </div>

      <div className="cmp-card">
        <h2 className="cmp-h2">Funnel <span>· {c.totalRecipients} recipients</span></h2>
        <div className="cmp-funnel">
          {funnel.map(f => (
            <div className="cmp-fn-row" key={f.label}>
              <div className="cmp-fn-label">{f.label}</div>
              <div className="cmp-fn-bar">{f.n > 0 && <div className="cmp-fn-fill" style={{ width: `${Math.max(pct(f.n), 3)}%` }}><span>{f.n}</span></div>}</div>
              <div className="cmp-fn-pct">{pct(f.n)}%</div>
            </div>
          ))}
        </div>
      </div>

      <div className="cmp-card">
        <div className="cmp-tab-row">
          <h2 className="cmp-h2">Contacts</h2>
          <div className="cmp-filters">
            {['all', 'clicked', 'ordered'].map(f => <button key={f} className={`cmp-filter${filter === f ? ' on' : ''}`} onClick={() => setFilter(f)}>{f}</button>)}
          </div>
        </div>
        <div className="cmp-table-wrap cmp-contacts">
          <table className="cmp-table">
            <thead><tr><th>Contact</th><th>Sent</th><th>Delivered</th><th>Read</th><th>Clicked</th><th>Order</th><th>Order time</th></tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={7} className="cmp-none">No contacts in this view.</td></tr>
                : rows.map(r => (
                  <tr key={r._id}>
                    <td><b className="cmp-mono">+{r.phone}</b>{r.name ? <div className="cmp-muted">{r.name}</div> : null}</td>
                    <td className="cmp-muted">{fmt(r.sentAt)}</td>
                    <td className="cmp-muted">{fmt(r.deliveredAt)}</td>
                    <td className="cmp-muted">{fmt(r.readAt)}</td>
                    <td>{r.clickedAt ? <span className="cmp-yes">{fmt(r.clickedAt)}{r.clickCount > 1 ? ` (${r.clickCount}×)` : ''}</span> : <span className="cmp-muted">—</span>}</td>
                    <td>{r.orderId ? <span className="cmp-order">{r.orderCurrency} {Math.round(r.orderValue || 0)}{r.orderNumber ? ` · #${r.orderNumber}` : ''}</span> : <span className="cmp-muted">—</span>}</td>
                    <td className="cmp-muted">{fmt(r.orderedAt)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
